import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce, useWindowScroll } from 'react-use';
import { useAccount } from '~/features/club';
import { TranslationKey } from '~/lib/data-contract';
import { useTranslation } from '~/shared/utils';
import { useUIStateStore } from './useUIStateStore';
import { SECTIONS } from '../constants/sections';

/**
 * Handles which id is active, scrolls to valid section when ready
 */
export const useUIState = () => {
    const { setSections, sections } = useUIStateStore();
    const [activeId, setActiveId] = useState<SECTIONS>(SECTIONS.OVERVIEW);
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
    const { asPath } = useRouter();
    const { translate } = useTranslation();
    const { account } = useAccount();
    const { y } = useWindowScroll();
    const isSoftLoggedIn = account?.isSoftLoggedIn;

    // Replace danish letters to create a safe dom id
    const createSafeDomId = (str: string) =>
        str.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'oe').replace(/å/g, 'aa');

    // Define guard clauses to show/hide sections
    const sectionsGuard = useCallback(
        (key: string) => {
            const sectionKey = SECTIONS[key as keyof typeof SECTIONS];

            if (isSoftLoggedIn && sectionKey === SECTIONS.ACCOUNT) {
                return false;
            }
            return true;
        },
        [isSoftLoggedIn]
    );

    useEffect(() => {
        const mappedSections = Object.keys(SECTIONS)
            .filter(sectionsGuard)
            .map((key) => {
                const id = SECTIONS[key as keyof typeof SECTIONS];
                const translation = translate(`club.profile.${id}` as TranslationKey);
                return {
                    translation,
                    id,
                    domId: createSafeDomId(translation?.split(' ').join('-')),
                };
            });

        setSections(mappedSections);
    }, [sectionsGuard, setSections, translate]);

    useDebounce(
        () => {
            const sectionPositions = sections.map(
                ({ domId }) => document.getElementById(domId)?.offsetTop || 0
            );
            const closest = [...sectionPositions].sort(
                (a, b) => Math.abs(a - y) - Math.abs(b - y)
            )[0];
            const index = sectionPositions.indexOf(closest);

            setActiveId(sections[index]?.id);
        },
        50,
        [sections, y]
    );

    useEffect(() => {
        if (!account || !isInitialLoad) {
            return;
        }
        const [, id] = asPath.split('#');
        if (!id) {
            return;
        }

        setTimeout(() => {
            const decodedId = decodeURI(id);

            document.getElementById(decodedId)?.scrollIntoView({ behavior: 'smooth' });
            setActiveId(id as SECTIONS);
        });
        setIsInitialLoad(false);
    }, [account, asPath, isInitialLoad, sections, setActiveId]);

    useEffect(() => {
        document.documentElement.style.scrollBehavior = 'smooth';
        return () => {
            document.documentElement.style.scrollBehavior = '';
        };
    }, [sections]);

    return { activeId, sections };
};
